import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { RideSchema } from '../../types/simulation';
import { TripDemandChart } from './TripDemandChart';
import { RideMap } from './RideMap';

import { Icon } from '@liftango/ui-icons';
interface RideUploaderProps {
  onRidesUploaded: (rides: RideSchema[]) => void;
  rides: RideSchema[];
}

export const RideUploader: React.FC<RideUploaderProps> = ({ onRidesUploaded, rides }) => {
  const { t } = useTranslation('simulation');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      let parsedData: any[] = [];

      if (fileExtension === 'csv') {
        // Parse CSV
        Papa.parse(file, {
          header: true,
          complete: (results) => {
            parsedData = results.data as any[];
            processRideData(parsedData);
          },
          error: (error) => {
            setError(t('errors.csvParsing', { message: error.message }));
            setLoading(false);
          }
        });
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        // Parse Excel
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        parsedData = XLSX.utils.sheet_to_json(worksheet);
        processRideData(parsedData);
      } else {
        setError(t('errors.unsupportedFile'));
        setLoading(false);
      }
    } catch (err: any) {
      setError(t('errors.fileProcessing', { message: err.message }));
      setLoading(false);
    }
  }, [t]);

  const processRideData = (data: any[]) => {
    try {
      const rides: RideSchema[] = data
        .filter(row => row && Object.keys(row).length > 0)
        .map((row, index) => {
          const generateUUID = () => {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
              const r = (Math.random() * 16) | 0;
              const v = c === 'x' ? r : ((r & 0x3) | 0x8);
              return v.toString(16);
            });
          };

          // Helper to safely parse timestamps (timezone agnostic)
          const parseTimestamp = (timeValue: any): string => {
            if (!timeValue) {
              const now = new Date();
              return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}.000`;
            }
            
            if (typeof timeValue === 'string') {
              // Remove any timezone indicators to make it timezone agnostic
              const cleanTime = timeValue.replace(/[Z+-]\d{2}:\d{2}$|[Z]$/, '');
              
              // Try to parse the cleaned string as a date
              const parsed = new Date(cleanTime);
              if (!isNaN(parsed.getTime()) && parsed.getFullYear() > 2000) {
                // Return in format: YYYY-MM-DDTHH:mm:ss.000 (timezone agnostic)
                return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}T${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}:${String(parsed.getSeconds()).padStart(2, '0')}.000`;
              }
            }
            
            if (typeof timeValue === 'number') {
              // Handle Excel serial date numbers
              if (timeValue > 40000 && timeValue < 50000) {
                // Excel date serial number (days since 1900-01-01)
                const excelEpoch = new Date(1900, 0, 1);
                const date = new Date(excelEpoch.getTime() + (timeValue - 2) * 24 * 60 * 60 * 1000);
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}.000`;
              }
              
              // Unix timestamp handling
              if (timeValue > 1000000000) {
                // Looks like a proper timestamp
                const date = timeValue > 10000000000 ? new Date(timeValue) : new Date(timeValue * 1000);
                if (date.getFullYear() > 2000) {
                  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}.000`;
                }
              }
            }
            
            // Fallback to current time
            const now = new Date();
            return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}.000`;
          };

          return {
            external_reference_id: String(row.external_reference_id || row.ride_id || `ride-${index + 1}`),
            external_user_reference_id: String(row.external_user_reference_id || row.user_id || `user-${index + 1}`),
            from_latitude: parseFloat(row.from_latitude || row.pickup_lat || row.origin_lat),
            from_longitude: parseFloat(row.from_longitude || row.pickup_lng || row.origin_lng),
            to_latitude: parseFloat(row.to_latitude || row.dropoff_lat || row.destination_lat),
            to_longitude: parseFloat(row.to_longitude || row.dropoff_lng || row.destination_lng),
            from_address: String(row.from_address || row.pickup_address || ''),
            to_address: String(row.to_address || row.dropoff_address || ''),
            request_type: row.request_type || 'departure_fixed',
            passengers: [
              {
                number_of_passengers: Math.max(1, parseInt(row.passengers || row.passenger_count || '1')),
                type: (row.passenger_type || 'standard').toLowerCase()
              }
            ],
            request_time: parseTimestamp(row.request_time || row.pickup_time)
          };
        });

      // Debug logging for uploaded data
      console.log('🔍 Ride Upload Debug:');
      console.log('📊 Raw data rows:', data.length);
      console.log('📊 Processed rides:', rides.length);
      console.log('📊 Sample processed ride:', JSON.stringify(rides[0], null, 2));
      
      // Validate rides
      const validRides = rides.filter(ride => 
        !isNaN(ride.from_latitude) && 
        !isNaN(ride.from_longitude) && 
        !isNaN(ride.to_latitude) && 
        !isNaN(ride.to_longitude)
      );

      console.log('📊 Valid rides after filtering:', validRides.length);
      if (validRides.length > 0) {
        console.log('📊 Sample valid ride:', JSON.stringify(validRides[0], null, 2));
      }

      if (validRides.length === 0) {
        throw new Error(t('errors.noValidRides'));
      }

      onRidesUploaded(validRides);
      setError(null);
    } catch (err: any) {
      setError(t('errors.dataProcessing', { message: err.message }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('setup.rideUploader.title')}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('setup.rideUploader.processing')}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            component="label"
            startIcon={<Icon size={20} name="upload" />}
            disabled={loading}
          >
            {t('setup.rideUploader.uploadButton')}
            <input
              type="file"
              hidden
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
            />
          </Button>
        </Box>

        {rides.length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              {t('setup.rideUploader.loadedRides')} <Chip label={rides.length} color="primary" size="small" />
            </Typography>
            
            {/* Rides Table */}
            <Box sx={{ height: 400, width: '100%', mb: 2 }}>
              <DataGrid
                rows={rides.map((ride, index) => ({
                  id: index,
                  from_address: ride.from_address || t('setup.rideUploader.fallback.origin'),
                  to_address: ride.to_address || t('setup.rideUploader.fallback.destination'),
                  passengers: ride.passengers[0]?.number_of_passengers || 1,
                  passenger_type: ride.passengers[0]?.type || 'standard',
                  request_type: ride.request_type || 'departure_fixed',
                  request_time: ride.request_time ? new Date(ride.request_time).toLocaleString() : t('setup.rideUploader.fallback.na'),
                  from_latitude: ride.from_latitude.toFixed(6),
                  from_longitude: ride.from_longitude.toFixed(6),
                  to_latitude: ride.to_latitude.toFixed(6),
                  to_longitude: ride.to_longitude.toFixed(6)
                }))}
                columns={[
                  {
                    field: 'from_address',
                    headerName: t('setup.rideUploader.columns.from'),
                    width: 150,
                    flex: 1
                  },
                  {
                    field: 'to_address',
                    headerName: t('setup.rideUploader.columns.to'),
                    width: 150,
                    flex: 1
                  },
                  {
                    field: 'from_latitude',
                    headerName: t('setup.rideUploader.columns.fromLat'),
                    width: 110,
                    type: 'number'
                  },
                  {
                    field: 'from_longitude',
                    headerName: t('setup.rideUploader.columns.fromLng'),
                    width: 110,
                    type: 'number'
                  },
                  {
                    field: 'to_latitude',
                    headerName: t('setup.rideUploader.columns.toLat'),
                    width: 110,
                    type: 'number'
                  },
                  {
                    field: 'to_longitude',
                    headerName: t('setup.rideUploader.columns.toLng'),
                    width: 110,
                    type: 'number'
                  },
                  {
                    field: 'passengers',
                    headerName: t('setup.rideUploader.columns.passengers'),
                    width: 100,
                    type: 'number'
                  },
                  {
                    field: 'passenger_type',
                    headerName: t('setup.rideUploader.columns.type'),
                    width: 100,
                    renderCell: (params) => (
                      <Chip
                        label={params.value}
                        size="small"
                        color={params.value === 'wheelchair' ? 'secondary' : 'default'}
                      />
                    )
                  },
                  {
                    field: 'request_type',
                    headerName: t('setup.rideUploader.columns.requestType'),
                    width: 120
                  },
                  {
                    field: 'request_time',
                    headerName: t('setup.rideUploader.columns.requestTime'),
                    width: 180
                  }
                ]}
                pageSizeOptions={[5, 10, 25, 50]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 }
                  }
                }}
                disableRowSelectionOnClick
                sx={{
                  '& .MuiDataGrid-cell': {
                    fontSize: '0.875rem'
                  },
                  '& .MuiDataGrid-columnHeader': {
                    fontSize: '0.875rem',
                    fontWeight: 'bold'
                  }
                }}
              />
            </Box>

            {/* Trip Demand Chart and Map */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', lg: 'row' } }}>
              <Box sx={{ flex: { xs: 1, lg: 1 } }}>
                <TripDemandChart rides={rides} />
              </Box>
              <Box sx={{ flex: { xs: 1, lg: 1.5 } }}>
                <RideMap rides={rides} />
              </Box>
            </Box>
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          {t('setup.rideUploader.expectedColumns')}
        </Typography>
      </CardContent>
    </Card>
  );
};
